import { PackageData } from "@/pages/CreatePackage";
import { TemplateStyle } from "../types";
import { companyName } from "../constants";
import { generateQrCode } from "../helpers";
import { renderCommonSection } from "../commonSections";

const renderSection = (sectionId: string, packageData: PackageData): string => {
  const common = renderCommonSection(sectionId, packageData);
  if (common !== null) return common;

  switch (sectionId) {
    case 'client': {
      const clientCompanyLogoHtml = packageData.companyLogoUrl ? `<img src="${packageData.companyLogoUrl}" alt="شعار شركة العميل" style="max-height: 50px; margin-right: 15px; border-radius: 8px; vertical-align: middle;" />` : '';
      const companyNameHtml = packageData.companyName ? `<span style="vertical-align: middle;"><strong>الشركة:</strong> ${packageData.companyName}</span>` : '';
      const companyInfoHtml = (packageData.companyName || packageData.companyLogoUrl) ? `<div class="item" style="grid-column: 1 / -1; margin-bottom: 10px;">${clientCompanyLogoHtml}${companyNameHtml}</div>` : '';
      
      return `<div class="section"><h3>معلومات العميل</h3>
                <div class="grid">
                  ${companyInfoHtml}
                  <div class="item"><strong>الاسم:</strong> ${packageData.clientName}</div>
                  <div class="item"><strong>البريد:</strong> ${packageData.clientEmail}</div>
                  <div class="item"><strong>الهاتف:</strong> ${packageData.clientPhone}</div>
                  <div class="item"><strong>النوع:</strong> ${packageData.clientType === 'b2b' ? 'شراكة تجارية' : 'عميل فردي'}</div>
                </div>
              </div>`;
    }
    case 'hotels':
      if (packageData.hotels.length === 0) return '';
      return `<div class="section">
                <h3>🏨 الفنادق والإقامة</h3>
                ${packageData.hotels.map(hotel => `
                  <div class="item" style="margin-bottom: 15px;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                      <strong style="font-size: 18px;">${hotel.name}</strong>
                      <span style="background: #007bff; color: white; padding: 4px 12px; border-radius: 20px; font-size: 12px;">${hotel.nights} ليالي</span>
                    </div>
                    <div style="color: #666; margin-bottom: 5px;">📍 ${hotel.location}</div>
                    <div style="color: #666; margin-bottom: 5px;">🛏️ نوع الغرفة: ${hotel.roomType || 'قياسي'}</div>
                    <div style="display: flex; justify-content: space-between; padding-top: 8px; border-top: 1px dashed #ddd;">
                      <span>📅 من ${hotel.checkIn} إلى ${hotel.checkOut}</span>
                      <strong style="color: #28a745;">$${hotel.pricePerNight * hotel.nights}</strong>
                    </div>
                  </div>
                `).join('')}
              </div>`;
    case 'flights':
      if (!packageData.flights || packageData.flights.length === 0) return '';
      return `<div class="section">
                <h3>✈️ الرحلات الجوية</h3>
                ${packageData.flights.map(flight => `
                  <div class="item" style="margin-bottom: 15px; background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                      <strong style="font-size: 16px;">${flight.airline || 'طيران'}</strong>
                      <span style="background: ${flight.type === 'outbound' ? '#007bff' : '#28a745'}; color: white; padding: 3px 10px; border-radius: 15px; font-size: 11px;">
                        ${flight.type === 'outbound' ? '🛫 ذهاب' : flight.type === 'return' ? '🛬 عودة' : '✈️ داخلي'}
                      </span>
                    </div>
                    <div style="display: flex; justify-content: space-between; align-items: center; margin: 12px 0;">
                      <div style="text-align: center; flex: 1;">
                        <div style="font-weight: bold; font-size: 16px; color: #333;">${flight.from}</div>
                        <div style="font-size: 12px; color: #666;">${flight.fromCity || ''}</div>
                      </div>
                      <div style="flex: 0 0 80px; text-align: center;">
                        <div style="border-top: 2px solid #007bff; position: relative; margin: 8px 0;">
                          <span style="position: absolute; top: -10px; left: 50%; transform: translateX(-50%); background: white; padding: 0 8px;">✈️</span>
                        </div>
                        <div style="font-size: 11px; color: #999;">${flight.class || 'اقتصادي'}</div>
                      </div>
                      <div style="text-align: center; flex: 1;">
                        <div style="font-weight: bold; font-size: 16px; color: #333;">${flight.to}</div>
                        <div style="font-size: 12px; color: #666;">${flight.toCity || ''}</div>
                      </div>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding-top: 10px; border-top: 1px dashed #ddd; font-size: 13px;">
                      <span>📅 ${flight.date}${flight.time ? ` الساعة ${flight.time}` : ''}</span>
                      <span>👥 ${(flight.adults || 0) + (flight.children || 0) + (flight.infants || 0)} مسافر</span>
                      <strong style="color: #28a745;">$${flight.price || 0}</strong>
                    </div>
                    ${flight.transitFlight ? `<div style="margin-top: 8px; padding: 6px; background: #fff3cd; border-radius: 4px; font-size: 12px;">⏱️ رحلة عبور: ${flight.transitDuration || 'غير محدد'} ساعة</div>` : ''}
                  </div>
                `).join('')}
              </div>`;
    case 'transport':
      if (!packageData.transport || packageData.transport.length === 0) return '';
      return `<div class="section">
                <h3>🚗 النقل والمواصلات</h3>
                ${packageData.transport.map(item => `
                  <div class="item" style="margin-bottom: 12px;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                      <strong>
                        ${item.type === 'airport_transfer' ? '🚖 نقل من/إلى المطار' : item.type === 'city_transport' ? '🚌 تنقل بين المدن' : '🚙 جولة'}
                      </strong>
                      <span style="background: #6c757d; color: white; padding: 3px 10px; border-radius: 12px; font-size: 11px;">${item.vehicle || 'سيارة خاصة'}</span>
                    </div>
                    <div style="display: flex; align-items: center; margin: 8px 0; color: #666;">
                      <span style="flex: 1;">${item.from}</span>
                      <span style="margin: 0 10px;">→</span>
                      <span style="flex: 1; text-align: left;">${item.to}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding-top: 6px; border-top: 1px dashed #ddd; font-size: 13px;">
                      <span>📅 ${item.date || 'حسب الجدول'}</span>
                      <strong style="color: #28a745;">$${item.price || 0}</strong>
                    </div>
                  </div>
                `).join('')}
              </div>`;
    case 'tours':
      if (!packageData.tours || packageData.tours.length === 0) return '';
      return `<div class="section">
                <h3>🎯 الجولات السياحية</h3>
                ${packageData.tours.map(tour => `
                  <div class="item" style="margin-bottom: 15px; border-right: 4px solid #007bff;">
                    <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 10px;">
                      <div style="flex: 1;">
                        <strong style="font-size: 16px; color: #333; display: block; margin-bottom: 5px;">${tour.name}</strong>
                        <p style="margin: 0; color: #666; font-size: 13px; line-height: 1.5;">${tour.description || ''}</p>
                      </div>
                    </div>
                    ${tour.included && tour.included.length > 0 ? `
                      <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; margin: 10px 0;">
                        <div style="font-size: 12px; font-weight: bold; color: #555; margin-bottom: 5px;">✓ يشمل:</div>
                        <div style="font-size: 12px; color: #666;">
                          ${tour.included.map(item => `<span style="display: inline-block; margin: 2px 5px 2px 0;">• ${item}</span>`).join('')}
                        </div>
                      </div>
                    ` : ''}
                    <div style="display: flex; justify-content: space-between; padding-top: 8px; border-top: 1px dashed #ddd; font-size: 13px;">
                      <span>📅 ${tour.date || 'حسب الجدول'}</span>
                      <span>⏱️ ${tour.duration || 'يوم كامل'}</span>
                      <strong style="color: #28a745;">$${tour.price || 0}</strong>
                    </div>
                  </div>
                `).join('')}
              </div>`;
    case 'price':
      return `<div class="price">إجمالي السعر: $${packageData.totalPrice}</div>`;
    default:
      return '';
  }
};

export const getTemplate1HTML = async (packageData: PackageData, settings: TemplateStyle): Promise<string> => {
  const columnsHtml = settings.columns.map(column => {
    const sectionsHtml = column.sections.map(section => renderSection(section.id, packageData)).join('');
    return `<div class="pdf-column">${sectionsHtml}</div>`;
  }).join('');

  const qrCodeData = await generateQrCode(`Package: ${packageData.packageName}\nClient: ${packageData.clientName}\nPrice: $${packageData.totalPrice}`);
  
  const logoHtml = settings.media?.logo ? `<img src="${settings.media.logo}" alt="Company Logo" style="max-height: 70px; margin: 0 auto 20px auto; display: block;" />` : '';
  const backgroundStyle = settings.media?.background ? `background: url('${settings.media.background}') center center / cover no-repeat;` : '';

  return `
    <!DOCTYPE html>
    <html dir="rtl" lang="ar">
    <head>
      <meta charset="UTF-8">
      <title>عرض سياحي - ${packageData.packageName}</title>
      <style>
        * { font-family: 'Arial', sans-serif; }
        body { margin: 0; padding: 20px; background: #f0f2f5; }
        .container { max-width: 800px; margin: 0 auto; background: white; border-radius: 15px; overflow: hidden; box-shadow: 0 20px 40px rgba(0,0,0,0.1); ${backgroundStyle} }
        .header { background: ${settings.colors.primary}; color: ${settings.colors.text}; padding: 30px; text-align: center; }
        .company-name { font-size: 16px; opacity: 0.8; margin-bottom: 10px; }
        .header h1 { font-size: 28px; margin: 0; margin-bottom: 10px; }
        .header p { font-size: 16px; opacity: 0.9; margin: 0; }
        .content { padding: 30px; }
        .pdf-row { display: flex; flex-direction: row; gap: 20px; }
        .pdf-column { flex: 1; min-width: 0; }
        .section { margin-bottom: 30px; padding: 20px; border-radius: 10px; background: ${settings.colors.background}; }
        .section h3 { color: #333; margin-top: 0; margin-bottom: 15px; font-size: 20px; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        .item { background: white; padding: 15px; border-radius: 8px; border-right: 4px solid ${settings.colors.primary}; }
        .price { font-size: 24px; font-weight: bold; color: white; text-align: center; padding: 20px; background: linear-gradient(135deg, #28a745, #20c997); border-radius: 10px; margin: 20px 0; }
        .footer { text-align: center; color: #666; padding: 20px; font-size: 14px; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          ${logoHtml}
          <div class="company-name">${companyName}</div>
          <h1>${packageData.packageName || 'عرض سياحي مخصص'}</h1>
          <p>${packageData.destination} - ${packageData.duration}</p>
        </div>
        <div class="content">
          <div class="pdf-row">
            ${columnsHtml}
          </div>
          <div class="footer">
            <img src="${qrCodeData}" alt="QR Code" style="width:80px;height:80px;margin:10px auto;" />
            تم إنشاء هذا العرض بواسطة نظام إدارة السياحة<br>
            تاريخ الإنشاء: ${new Date().toLocaleDateString('ar-SA')}
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};
